import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "./api/client";
import AdvancedFiltersModal from "./components/AdvancedFiltersModal";
import AddToTradeQuantityControl from "./components/AddToTradeQuantityControl";
import CardGrid from "./components/CardGrid";
import AccountSettingSelect from "./components/AccountSettingSelect";
import UserIdentityLabel from "./components/UserIdentityLabel";
import { renderConditionOption, renderEditionOption, renderLanguageOption } from "./components/dropdownOptionRenderers";
import { useCurrentUser } from "./currentUserContext";
import CardsTable from "./features/traders/components/CardsTable";
import { DEFAULT_SORT as DEFAULT_TABLE_SORT } from "./features/traders/components/CardsTable";
import { getCountryOptionByName } from "./locationOptions";
import usePaginationScroll from "./utils/usePaginationScroll";
import { addTradeDraftRequestCard, getRequestedTradeDraftQty, mergeTradeDraftRequestCards, serializeTradeDraftCardForRequest } from "./utils/tradeDraftRequestCards";
import { buildBrowseCardPath } from "./utils/buildBrowseCardPath";
import { buildBrowseUserPath } from "./utils/browseUserPath";

const CARDS_PER_PAGE = 50;
const MOBILE_BROWSE_USERS_BREAKPOINT = 780;
const FILTER_META_DEFAULT = {
  editions: [],
  languages: [],
  conditions: [],
  priceRange: {
    min: 0,
    max: 0
  }
};
const FOIL_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "nonfoil", label: "Non-foil only" },
  { value: "foil", label: "Foil only" }
];
const ALTERED_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "notaltered", label: "Not altered" },
  { value: "altered", label: "Altered only" }
];
const SIGNED_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "unsigned", label: "Not signed" },
  { value: "signed", label: "Signed only" }
];

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

function clampNumber(value, min, max, fallback) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(Math.max(numericValue, min), max);
}

function getRangePercent(value, min, max) {
  if (max <= min) {
    return 0;
  }

  return ((value - min) / (max - min)) * 100;
}

function PriceRangeSlider({ min, max, step = 0.5, valueMin, valueMax, disabled, onChange }) {
  const lowerValue = Math.min(valueMin, valueMax);
  const upperValue = Math.max(valueMin, valueMax);
  const leftPercent = getRangePercent(lowerValue, min, max);
  const rightPercent = getRangePercent(upperValue, min, max);

  return (
    <div className={`price-range-slider${disabled ? " price-range-slider--disabled" : ""}`}>
      <div className="price-range-slider__track" aria-hidden="true" />
      <div
        className="price-range-slider__range"
        aria-hidden="true"
        style={{
          left: `${leftPercent}%`,
          width: `${Math.max(rightPercent - leftPercent, 0)}%`
        }}
      />
      <input
        className="price-range-slider__input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={lowerValue}
        aria-label="Minimum price"
        disabled={disabled}
        onChange={(event) => {
          const nextValue = Math.min(Number(event.target.value), upperValue);
          onChange({ min: nextValue, max: upperValue });
        }}
      />
      <input
        className="price-range-slider__input price-range-slider__input--upper"
        type="range"
        min={min}
        max={max}
        step={step}
        value={upperValue}
        aria-label="Maximum price"
        disabled={disabled}
        onChange={(event) => {
          const nextValue = Math.max(Number(event.target.value), lowerValue);
          onChange({ min: lowerValue, max: nextValue });
        }}
      />
    </div>
  );
}

function buildCollectionFilterPayload({
  searchTerm,
  editionFilter,
  languageFilter,
  conditionFilter,
  foilFilter,
  alteredFilter,
  signedFilter,
  priceMin,
  priceMax,
  effectivePriceMin,
  effectivePriceMax
}) {
  return {
    search: searchTerm ? String(searchTerm).trim() : "",
    edition: editionFilter || "",
    language: languageFilter || "",
    condition: conditionFilter || "",
    foil: foilFilter || "all",
    altered: alteredFilter || "all",
    signed: signedFilter || "all",
    minPrice: priceMin !== null ? effectivePriceMin : null,
    maxPrice: priceMax !== null ? effectivePriceMax : null
  };
}

function createPaginationState(page = 1) {
  return {
    page,
    limit: CARDS_PER_PAGE,
    totalItems: 0,
    totalPages: 1,
    totalCards: 0
  };
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis-right", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "ellipsis-left", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis-left", currentPage - 1, currentPage, currentPage + 1, "ellipsis-right", totalPages];
}

function PaginationControls({ currentPage, totalItems, totalPages, label, onPageChange, position = "top", disabled = false }) {
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * CARDS_PER_PAGE + 1;
  const endItem = Math.min(totalItems, currentPage * CARDS_PER_PAGE);
  const pageNumbers = getVisiblePageNumbers(currentPage, totalPages);
  const isBottom = position === "bottom";

  const commitPageInput = () => {
    const nextPage = Number.parseInt(pageInput, 10);

    if (!Number.isFinite(nextPage)) {
      setPageInput(String(currentPage));
      return;
    }

    const clampedPage = Math.min(Math.max(nextPage, 1), totalPages);
    setPageInput(String(clampedPage));

    if (clampedPage !== currentPage) {
      onPageChange(clampedPage);
    }
  };

  return (
    <div
      className="app-pagination"
      style={{
        marginTop: isBottom ? "18px" : 0,
        marginBottom: isBottom ? 0 : "18px"
      }}
    >
      <div className="app-pagination__summary">
        {label}: {startItem}-{endItem} of {totalItems}
      </div>
      <div className="app-pagination__controls">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
          title="Previous page"
          disabled={disabled || currentPage === 1}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: disabled || currentPage === 1 ? "#f8fafc" : "#ffffff",
            color: disabled || currentPage === 1 ? "#94a3b8" : "#0f172a",
            cursor: disabled || currentPage === 1 ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{"<"}</span>
        </button>
        <div className="app-pagination__page-list">
          {pageNumbers.map((pageNumber) => {
            if (typeof pageNumber !== "number") {
              return <span key={pageNumber} className="app-pagination__ellipsis">...</span>;
            }

            const isActive = pageNumber === currentPage;

            return (
              <button
                key={pageNumber}
                type="button"
                onClick={() => onPageChange(pageNumber)}
                disabled={disabled}
                aria-current={isActive ? "page" : undefined}
                className="app-pagination__button"
                style={{
                  border: isActive ? "1px solid #0f766e" : "1px solid #cbd5e1",
                  background: isActive ? "#ccfbf1" : "#ffffff",
                  color: disabled ? "#94a3b8" : (isActive ? "#115e59" : "#334155"),
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.7 : 1
                }}
              >
                {pageNumber}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
          title="Next page"
          disabled={disabled || currentPage === totalPages}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: disabled || currentPage === totalPages ? "#f8fafc" : "#ffffff",
            color: disabled || currentPage === totalPages ? "#94a3b8" : "#0f172a",
            cursor: disabled || currentPage === totalPages ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{">"}</span>
        </button>
        <label className="app-pagination__jump">
          Page
          <div className="app-pagination__jump-controls">
            <div className="app-pagination__select-shell">
              <input
                type="number"
                min="1"
                max={String(totalPages)}
                step="1"
                inputMode="numeric"
                aria-label="Jump to page"
                className="app-pagination__page-input"
                value={pageInput}
                disabled={disabled}
                onChange={(event) => setPageInput(event.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitPageInput();
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    setPageInput(String(currentPage));
                    event.currentTarget.blur();
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="app-pagination__button app-pagination__button--text app-pagination__jump-button"
              onClick={commitPageInput}
              disabled={disabled}
            >
              Go
            </button>
          </div>
        </label>
      </div>
    </div>
  );
}

function parseBrowseUserId(value) {
  const parsedValue = Number.parseInt(String(value || ""), 10);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

function getTradeDraftCardTokens(card = {}) {
  const tokens = new Set();
  const numericListingId = Number(card.source_listing_id || card.id);

  if (Number.isInteger(numericListingId) && numericListingId > 0) {
    tokens.add(`listing:${numericListingId}`);
  }

  tokens.add([
    card.printing_id || "",
    String(card.language || "").trim().toLowerCase(),
    String(card.condition || "").trim().toUpperCase(),
    card.foil ? "foil" : "nonfoil",
    card.altered ? "altered" : "clean",
    card.signed ? "signed" : "unsigned",
    String(card.card || "").trim().toLowerCase()
  ].join(":"));

  return tokens;
}

function hasMatchingTradeDraftToken(card, existingTokens) {
  for (const token of getTradeDraftCardTokens(card)) {
    if (existingTokens.has(token)) {
      return true;
    }
  }

  return false;
}

export default function BrowseUsersView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { browseUserRef } = useParams();
  const { selectedUserId } = useCurrentUser();
  const userSearchFieldRef = useRef(null);
  const userSearchDropdownRef = useRef(null);
  const normalizedBrowseUserRef = String(browseUserRef || "").trim();
  const locationStateUserId = parseBrowseUserId(location.state?.userId);
  const routeBrowseUserId = parseBrowseUserId(normalizedBrowseUserRef);
  const [resolvedBrowseUser, setResolvedBrowseUser] = useState(null);
  const selectedBrowseUserId = resolvedBrowseUser?.id || locationStateUserId || routeBrowseUserId;
  const [usersSearchInput, setUsersSearchInput] = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchError, setUserSearchError] = useState("");
  const [featuredUsers, setFeaturedUsers] = useState([]);
  const [loadingFeaturedUsers, setLoadingFeaturedUsers] = useState(false);
  const [featuredUsersError, setFeaturedUsersError] = useState("");
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [userDropdownStyle, setUserDropdownStyle] = useState(null);
  const [loadingCards, setLoadingCards] = useState(false);
  const [error, setError] = useState(null);
  const [tradeDrafts, setTradeDrafts] = useState([]);
  const [tradeFinderSuggestions, setTradeFinderSuggestions] = useState([]);
  const [tradeDraftNotice, setTradeDraftNotice] = useState(null);
  const [isAddingToTrade, setIsAddingToTrade] = useState(false);
  const [addingTradeCardId, setAddingTradeCardId] = useState(null);
  const [cards, setCards] = useState([]);
  const [pagination, setPagination] = useState(() => createPaginationState());
  const [page, setPage] = useState(1);
  const [selectionCounts, setSelectionCounts] = useState({
    suggestedItems: 0,
    suggestedCards: 0,
    availableItems: 0,
    availableCards: 0
  });
  const [setMetadata, setSetMetadata] = useState({});
  const [filterMeta, setFilterMeta] = useState(FILTER_META_DEFAULT);
  const [viewMode, setViewMode] = useState("images");
  const [isMobileFeaturedUsersLayout, setIsMobileFeaturedUsersLayout] = useState(() => (typeof window !== "undefined"
    ? window.innerWidth <= MOBILE_BROWSE_USERS_BREAKPOINT
    : false));
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editionFilter, setEditionFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [foilFilter, setFoilFilter] = useState("all");
  const [alteredFilter, setAlteredFilter] = useState("all");
  const [signedFilter, setSignedFilter] = useState("all");
  const [showSuggestedCards, setShowSuggestedCards] = useState(true);
  const [showAvailableCards, setShowAvailableCards] = useState(true);
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [sortConfig, setSortConfig] = useState(DEFAULT_TABLE_SORT);
  const priceBounds = filterMeta.priceRange || FILTER_META_DEFAULT.priceRange;
  const rawPriceMin = clampNumber(priceMin, priceBounds.min, priceBounds.max, priceBounds.min);
  const rawPriceMax = clampNumber(priceMax, priceBounds.min, priceBounds.max, priceBounds.max);
  const effectivePriceMin = Math.min(rawPriceMin, rawPriceMax);
  const effectivePriceMax = Math.max(rawPriceMin, rawPriceMax);
  const canAdjustPriceRange = priceBounds.max > priceBounds.min;
  const trimmedUserSearchInput = usersSearchInput.trim();
  const activeAdvancedFilterCount = [
    Boolean(editionFilter),
    Boolean(languageFilter),
    Boolean(conditionFilter),
    foilFilter !== "all",
    alteredFilter !== "all",
    signedFilter !== "all"
  ].filter(Boolean).length;
  const resultsSummaryLabel = pagination.totalItems === 1 ? "matching stack" : "matching stacks";
  const showInitialLoadingState = loadingCards && cards.length === 0;
  const resultsSectionRef = usePaginationScroll(page);
  const hasBrowseUserSelection = Boolean(normalizedBrowseUserRef);

  const handleBrowsePageChange = (nextPage) => {
    if (loadingCards || nextPage === page || nextPage < 1 || nextPage > Math.max(1, Number(pagination.totalPages) || 1)) {
      return;
    }

    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setPage(nextPage);
  };

  const activeSuggestion = userSuggestions.find((user) => Number(user.id) === Number(selectedBrowseUserId)) || null;
  const activeUsername = resolvedBrowseUser?.username || activeSuggestion?.username || location.state?.username || (selectedBrowseUserId ? `User ${selectedBrowseUserId}` : hasBrowseUserSelection ? normalizedBrowseUserRef : "Browse users");
  const selectedBrowseDraft = tradeDrafts.find((draft) => Number(draft?.counterparty?.id) === Number(selectedBrowseUserId)) || null;
  const selectedBrowseSuggestion = tradeFinderSuggestions.find((suggestion) => Number(suggestion?.counterparty?.id) === Number(selectedBrowseUserId)) || null;
  const activeCollectorTier = resolvedBrowseUser?.collectorTier || activeSuggestion?.collectorTier || selectedBrowseDraft?.counterparty?.collectorTier || selectedBrowseSuggestion?.counterparty?.collectorTier || location.state?.collectorTier || null;
  const activeDisplayName = String(resolvedBrowseUser?.displayName || "").trim();
  const activeCity = String(resolvedBrowseUser?.city || "").trim();
  const activeCountry = String(resolvedBrowseUser?.country || "").trim();
  const activeCountryOption = getCountryOptionByName(activeCountry);
  const featuredUsersGridTemplate = "minmax(180px, 1.8fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(88px, 0.72fr)";
  const featuredUsersTableMinWidth = "544px";
  const selectedSuggestedTradeTokens = new Set(
    (selectedBrowseSuggestion?.receiveCards || []).flatMap((card) => Array.from(getTradeDraftCardTokens(card)))
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BROWSE_USERS_BREAKPOINT}px)`);

    const handleMediaQueryChange = (event) => {
      setIsMobileFeaturedUsersLayout(event.matches);
    };

    setIsMobileFeaturedUsersLayout(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaQueryChange);
      return () => mediaQuery.removeEventListener("change", handleMediaQueryChange);
    }

    mediaQuery.addListener(handleMediaQueryChange);
    return () => mediaQuery.removeListener(handleMediaQueryChange);
  }, []);

  const refreshTradeDrafts = async () => {
    const response = await apiFetch("/api/trade-drafts", { userId: selectedUserId });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || "Could not load trade drafts");
    }

    setTradeDrafts(Array.isArray(data.drafts) ? data.drafts : []);
  };

  const isCardAlreadyInDraft = (card) => {
    if (!selectedBrowseDraft && !selectedBrowseSuggestion) {
      return false;
    }

    const effectiveDemandCards = mergeTradeDraftRequestCards(
      selectedBrowseDraft?.demandCards || [],
      selectedBrowseSuggestion?.receiveCards || []
    );
    const requestedQty = getRequestedTradeDraftQty(effectiveDemandCards, card);
    const availableQty = Math.max(1, Number(card?.qty) || 1);

    return requestedQty >= availableQty;
  };

  const getBrowseCardRemainingQty = (card) => {
    const effectiveDemandCards = mergeTradeDraftRequestCards(
      selectedBrowseDraft?.demandCards || [],
      selectedBrowseSuggestion?.receiveCards || []
    );
    const requestedQty = getRequestedTradeDraftQty(effectiveDemandCards, card);
    const availableQty = Math.max(1, Number(card?.qty) || 1);

    return Math.max(0, availableQty - requestedQty);
  };

  const getCardTradeBlockReason = (card) => {
    if (!selectedBrowseDraft && hasMatchingTradeDraftToken(card, selectedSuggestedTradeTokens) && isCardAlreadyInDraft(card)) {
      return "Already in suggested trade";
    }

    if (isCardAlreadyInDraft(card)) {
      return "Already in trade draft";
    }

    return null;
  };

  const isBrowseCardDisabled = (card) => Boolean(getCardTradeBlockReason(card));

  const decorateBrowseCard = (card) => {
    const blockReason = getCardTradeBlockReason(card);
    const displayQty = getBrowseCardRemainingQty(card);

    if (!blockReason) {
      return {
        ...card,
        displayQty
      };
    }

    return {
      ...card,
      displayQty,
      source_listing_id: card.source_listing_id || card.id,
      isBrowseTradeBlocked: true,
      browseTradeBlockedLabel: blockReason === "Already in suggested trade" ? "In suggestion" : "In draft"
    };
  };

  const browseCardsWithTradeState = cards.map((card) => {
    const blockReason = getCardTradeBlockReason(card);
    const browseCardPath = buildBrowseCardPath(card.card || card.name);

    return {
      ...card,
      source_listing_id: card.source_listing_id || card.id,
      browseCardPath,
      tradeBlockReason: blockReason,
      isSuggestedTradeCard: Boolean(blockReason),
      isAvailableTradeCard: !blockReason,
      isBrowseTradeBlocked: Boolean(blockReason),
      browseTradeBlockedLabel: blockReason === "Already in suggested trade" ? "In suggestion" : (blockReason === "Already in trade draft" ? "In draft" : null)
    };
  });

  const suggestedCardCount = browseCardsWithTradeState.filter((card) => card.isSuggestedTradeCard).length;
  const availableCardCount = browseCardsWithTradeState.filter((card) => card.isAvailableTradeCard).length;
  const visibleBrowseCards = browseCardsWithTradeState;
  const suggestedItemsCount = Number(selectionCounts.suggestedItems) || suggestedCardCount;
  const availableItemsCount = Number(selectionCounts.availableItems) || availableCardCount;

  const handleToggleShowSuggestedCards = () => {
    setShowSuggestedCards((value) => !value);
  };

  const handleToggleShowAvailableCards = () => {
    setShowAvailableCards((value) => !value);
  };

  const tradeSelectionMode = showSuggestedCards && showAvailableCards
    ? "all"
    : showSuggestedCards
      ? "suggested"
      : showAvailableCards
        ? "available"
        : "none";

  const blockedListingIds = Array.from(new Set([
    ...(selectedBrowseSuggestion?.receiveCards || []).map((card) => Number(card.source_listing_id || card.id)),
    ...(selectedBrowseDraft?.demandCards || []).map((card) => Number(card.source_listing_id || card.id))
  ].filter((id) => Number.isInteger(id) && id > 0)));
  const blockedListingIdsParam = blockedListingIds.join(",");

  useEffect(() => {
    if (hasBrowseUserSelection) {
      return undefined;
    }

    let isDisposed = false;

    const loadFeaturedUsers = async () => {
      try {
        setLoadingFeaturedUsers(true);
        setFeaturedUsersError("");

        const response = await apiFetch("/api/traders/browse/users?limit=20&random=true", { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load featured users");
        }

        if (!isDisposed) {
          setFeaturedUsers(Array.isArray(data.users) ? data.users : []);
        }
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Featured users load error:", loadError);
          setFeaturedUsers([]);
          setFeaturedUsersError(loadError.message || "Could not load featured users");
        }
      } finally {
        if (!isDisposed) {
          setLoadingFeaturedUsers(false);
        }
      }
    };

    loadFeaturedUsers();

    return () => {
      isDisposed = true;
    };
  }, [hasBrowseUserSelection, selectedUserId]);

  useEffect(() => {
    let isDisposed = false;

    const loadTradeFinderSuggestions = async () => {
      try {
        const response = await apiFetch(`/api/tradefinder/${selectedUserId}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load trade suggestions");
        }

        if (!isDisposed) {
          setTradeFinderSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        }
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Browse trade suggestions load error:", loadError);
          setTradeFinderSuggestions([]);
        }
      }
    };

    const loadTradeDrafts = async () => {
      try {
        const response = await apiFetch("/api/trade-drafts", { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load trade drafts");
        }

        if (!isDisposed) {
          setTradeDrafts(Array.isArray(data.drafts) ? data.drafts : []);
        }
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Browse trade drafts load error:", loadError);
          setTradeDrafts([]);
        }
      }
    };

    loadTradeFinderSuggestions();
    loadTradeDrafts();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId]);

  useEffect(() => {
    if (hasBrowseUserSelection && activeUsername && activeUsername !== `User ${selectedBrowseUserId}`) {
      setUsersSearchInput(activeUsername);
    }
  }, [activeUsername, hasBrowseUserSelection, selectedBrowseUserId]);

  useEffect(() => {
    if (!normalizedBrowseUserRef) {
      setResolvedBrowseUser(null);
      return;
    }

    setResolvedBrowseUser((currentUser) => {
      if (routeBrowseUserId) {
        if (currentUser?.id === routeBrowseUserId) {
          return currentUser;
        }

        return {
          id: routeBrowseUserId,
          username: location.state?.username || "",
          collectorTier: location.state?.collectorTier || null
        };
      }

      if (currentUser?.username && currentUser.username.toLowerCase() === normalizedBrowseUserRef.toLowerCase()) {
        return currentUser;
      }

      if (location.state?.username && String(location.state.username).trim().toLowerCase() === normalizedBrowseUserRef.toLowerCase()) {
        return {
          id: locationStateUserId,
          username: location.state.username,
          collectorTier: location.state?.collectorTier || null
        };
      }

      return null;
    });
  }, [location.state, locationStateUserId, normalizedBrowseUserRef, routeBrowseUserId]);

  useEffect(() => {
    if (!isUserDropdownOpen) {
      setUserDropdownStyle(null);
      return undefined;
    }

    const updateDropdownPosition = () => {
      const element = userSearchFieldRef.current;

      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      setUserDropdownStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(180, window.innerHeight - rect.bottom - 24),
        zIndex: 1400
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [isUserDropdownOpen, userSuggestions.length, loadingUsers, userSearchError]);

  useEffect(() => {
    if (!isUserDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (userSearchFieldRef.current?.contains(event.target) || userSearchDropdownRef.current?.contains(event.target)) {
        return;
      }

      setIsUserDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isUserDropdownOpen]);

  useEffect(() => {
    let isDisposed = false;

    const loadUsers = async () => {
      try {
        if (trimmedUserSearchInput.length < 3) {
          setUserSuggestions([]);
          setLoadingUsers(false);
          setUserSearchError("");
          return;
        }

        setLoadingUsers(true);
        setUserSearchError("");

        const response = await apiFetch(`/api/traders/browse/users?q=${encodeURIComponent(trimmedUserSearchInput)}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load browse users");
        }

        if (isDisposed) {
          return;
        }

        setUserSuggestions(Array.isArray(data.users) ? data.users : []);
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Browse users load error:", loadError);
          setUserSuggestions([]);
          setUserSearchError(loadError.message || "Could not load browse users");
        }
      } finally {
        if (!isDisposed) {
          setLoadingUsers(false);
        }
      }
    };

    loadUsers();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId, trimmedUserSearchInput]);

  useEffect(() => {
    setPage(1);
    setPagination(createPaginationState(1));
  }, [normalizedBrowseUserRef]);

  useEffect(() => {
    setPage(1);
    setPagination((currentState) => createPaginationState(1));
  }, [searchTerm, editionFilter, languageFilter, conditionFilter, foilFilter, alteredFilter, signedFilter, priceMin, priceMax]);

  useEffect(() => {
    setPage(1);
    setPagination((currentState) => createPaginationState(1));
  }, [sortConfig.direction, sortConfig.key]);

  useEffect(() => {
    if (!normalizedBrowseUserRef) {
      setCards([]);
      setPage(1);
      setPagination(createPaginationState());
      setFilterMeta(FILTER_META_DEFAULT);
      setResolvedBrowseUser(null);
      return undefined;
    }

    let isDisposed = false;

    const loadCards = async () => {
      try {
        setLoadingCards(true);
        setError(null);

        const params = new URLSearchParams({
          page: String(page),
          limit: String(CARDS_PER_PAGE),
          includeUnlisted: "false",
          includeListed: "true"
        });
        const filters = buildCollectionFilterPayload({
          searchTerm,
          editionFilter,
          languageFilter,
          conditionFilter,
          foilFilter,
          alteredFilter,
          signedFilter,
          priceMin,
          priceMax,
          effectivePriceMin,
          effectivePriceMax
        });

        Object.entries(filters).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== "" && value !== "all") {
            params.set(key, String(value));
          }
        });

        params.set("tradeSelectionMode", tradeSelectionMode);

        if (blockedListingIdsParam) {
          params.set("blockedListingIds", blockedListingIdsParam);
        }

        if (sortConfig.key) {
          params.set("sortBy", sortConfig.key);
          params.set("sortDirection", sortConfig.direction);
        }

        const response = await apiFetch(`/api/traders/${encodeURIComponent(normalizedBrowseUserRef)}/collection?${params.toString()}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load user traders");
        }

        if (isDisposed) {
          return;
        }

        setResolvedBrowseUser(data.targetUser ? {
          id: Number(data.targetUser.id) || null,
          username: data.targetUser.username || "",
          collectorTier: data.targetUser.collectorTier || null,
          displayName: data.targetUser.displayName || "",
          city: data.targetUser.city || "",
          country: data.targetUser.country || ""
        } : null);
        setCards(Array.isArray(data.cards) ? data.cards : []);
        const nextPagination = data.pagination || createPaginationState(page);
        setPagination(nextPagination);
        if (nextPagination.page !== page) {
          setPage(nextPagination.page);
        }
        setSelectionCounts(data.selectionCounts || {
          suggestedItems: 0,
          suggestedCards: 0,
          availableItems: 0,
          availableCards: 0
        });
        setFilterMeta(data.filterMeta || FILTER_META_DEFAULT);
      } catch (loadError) {
        if (!isDisposed) {
          console.error("Browse user traders load error:", loadError);
          setError(loadError.message || "Could not load user traders");
          setSelectionCounts({
            suggestedItems: 0,
            suggestedCards: 0,
            availableItems: 0,
            availableCards: 0
          });
        }
      } finally {
        if (!isDisposed) {
          setLoadingCards(false);
        }
      }
    };

    loadCards();

    return () => {
      isDisposed = true;
    };
  }, [
    alteredFilter,
    conditionFilter,
    editionFilter,
    effectivePriceMax,
    effectivePriceMin,
    foilFilter,
    languageFilter,
    page,
    priceMax,
    priceMin,
    searchTerm,
    blockedListingIdsParam,
    showAvailableCards,
    showSuggestedCards,
    normalizedBrowseUserRef,
    selectedUserId,
    signedFilter,
    sortConfig.direction,
    sortConfig.key,
    tradeSelectionMode
  ]);

  useEffect(() => {
    const setCodes = [...new Set((filterMeta.editions || []).map((edition) => normalizeLookupValue(edition.code)).filter(Boolean))];
    const missingSetCodes = setCodes.filter((setCode) => !setMetadata[setCode]);

    if (missingSetCodes.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    apiFetch(`/api/traders/search/sets?codes=${encodeURIComponent(missingSetCodes.join(","))}`, { userId: selectedUserId })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not fetch set metadata");
        }

        if (isCancelled) {
          return;
        }

        setSetMetadata((current) => ({
          ...current,
          ...(data.sets || {})
        }));
      })
      .catch((fetchError) => {
        console.error("Browse users set metadata error:", fetchError);
      });

    return () => {
      isCancelled = true;
    };
  }, [filterMeta.editions, selectedUserId, setMetadata]);

  const handleTableSortChange = (nextSortConfig) => {
    setSortConfig(nextSortConfig);
  };

  const shouldShowUserDropdown = isUserDropdownOpen && (trimmedUserSearchInput.length >= 3 || loadingUsers || userSearchError || userSuggestions.length > 0);
  const userDropdown = shouldShowUserDropdown && userDropdownStyle
    ? createPortal(
        <div
          ref={userSearchDropdownRef}
          style={{
            ...userDropdownStyle,
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: "14px",
            boxShadow: "0 18px 30px rgba(15, 23, 42, 0.12)",
            overflowY: "auto"
          }}
        >
          {loadingUsers ? (
            <div style={{ padding: "12px 14px", color: "#64748b", fontSize: "14px" }}>
              Searching users...
            </div>
          ) : null}
          {!loadingUsers && userSearchError ? (
            <div style={{ padding: "12px 14px", color: "#b91c1c", fontSize: "14px" }}>
              {userSearchError}
            </div>
          ) : null}
          {!loadingUsers && !userSearchError && userSuggestions.length === 0 && trimmedUserSearchInput.length >= 3 ? (
            <div style={{ padding: "12px 14px", color: "#64748b", fontSize: "14px" }}>
              No users found.
            </div>
          ) : null}
          {!loadingUsers && !userSearchError && userSuggestions.map((user, index) => (
            <button
              key={user.id}
              type="button"
              onClick={() => {
                setUsersSearchInput(user.username);
                setIsUserDropdownOpen(false);
                navigate(buildBrowseUserPath(user.id, user.username), { state: { userId: user.id, username: user.username, collectorTier: user.collectorTier } });
              }}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px 14px",
                border: "none",
                background: "#fff",
                cursor: "pointer",
                fontSize: "14px",
                borderTop: index === 0 ? "none" : "1px solid #f1f5f9"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "baseline" }}>
                <UserIdentityLabel username={user.username} tier={user.collectorTier} size="sm" color="#0f172a" fontWeight={700} truncateAt={10} />
                <span style={{ fontSize: "12px", color: "#64748b" }}>{user.listedCount} cards</span>
              </div>
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  const handleBrowsePriceRangeChange = ({ min, max }) => {
    setPriceMin(min);
    setPriceMax(max);
  };

  const handleBrowsePriceInputChange = (bound, rawValue) => {
    const nextValue = rawValue === "" ? null : Number(rawValue);

    if (bound === "min") {
      setPriceMin(nextValue);
      return;
    }

    setPriceMax(nextValue);
  };

  const clearBrowseAdvancedFilters = () => {
    setEditionFilter("");
    setLanguageFilter("");
    setConditionFilter("");
    setFoilFilter("all");
    setAlteredFilter("all");
    setSignedFilter("all");
  };

  const handleAddCardToTrade = async (card, requestedQty = 1) => {
    if (!selectedBrowseUserId || !card || Number(selectedBrowseUserId) === Number(selectedUserId) || isBrowseCardDisabled(card)) {
      return;
    }

    const availableQty = Math.max(1, Number(card?.qty) || 1);
    const nextQty = Math.min(Math.max(1, Number.parseInt(String(requestedQty ?? 1), 10) || 1), availableQty);
    const hasSuggestedTrade = Boolean(selectedBrowseSuggestion);
    const nextOfferCards = hasSuggestedTrade
      ? mergeTradeDraftRequestCards(selectedBrowseDraft?.offerCards || [], selectedBrowseSuggestion.sendCards || [])
      : selectedBrowseDraft
        ? (selectedBrowseDraft.offerCards || []).map(serializeTradeDraftCardForRequest)
        : [];
    const baseDemandCards = hasSuggestedTrade
      ? mergeTradeDraftRequestCards(selectedBrowseDraft?.demandCards || [], selectedBrowseSuggestion.receiveCards || [])
      : mergeTradeDraftRequestCards(selectedBrowseDraft?.demandCards || []);
    const nextDemandCards = baseDemandCards.length > 0
      ? addTradeDraftRequestCard(baseDemandCards, card, nextQty)
      : null;

    try {
      setIsAddingToTrade(true);
      setAddingTradeCardId(card.source_listing_id || card.id);
      const response = await apiFetch(`/api/trade-drafts/${selectedBrowseUserId}/demand-cards`, {
        userId: selectedUserId,
        method: nextDemandCards ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: nextDemandCards
          ? JSON.stringify({
              offerCards: nextOfferCards,
              demandCards: nextDemandCards
            })
          : JSON.stringify({
              listingId: card.source_listing_id || card.id,
              qty: nextQty
            })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not add card to trade");
      }

      await refreshTradeDrafts();
      setTradeDraftNotice(
        hasSuggestedTrade
          ? `A suggested trade already existed. ${card.card || "Card"} was added to that suggested trade in Drafts. The draft expires after 4 hours if it is not proposed.`
          : `${card.card || "Card"} was added to the trade draft. The draft expires after 4 hours if it is not proposed.`
      );
    } catch (addError) {
      console.error("Browse add to trade error:", addError);
      setTradeDraftNotice(addError.message || "Could not add card to trade");
    } finally {
      setIsAddingToTrade(false);
      setAddingTradeCardId(null);
    }
  };

  const renderTradeActionButton = ({ card, cardDisabled }) => {
    const actionId = Number(card.source_listing_id || card.id);
    const isBusy = isAddingToTrade && Number(addingTradeCardId) === actionId;
    const isOwnBrowseUser = Number(selectedBrowseUserId) === Number(selectedUserId);

    return (
      <AddToTradeQuantityControl
        cardName={card.card}
        maxQty={Math.max(1, getBrowseCardRemainingQty(card))}
        disabled={isOwnBrowseUser || cardDisabled || isBusy}
        disabledReason={isOwnBrowseUser ? "You cannot trade your own cards" : (card.tradeBlockReason || "")}
        busy={isBusy}
        onAdd={(selectedQty) => {
          void handleAddCardToTrade(card, selectedQty);
        }}
      />
    );
  };

  return (
    <div className="account-shell app-responsive-shell app-responsive-stack browse-users-page">
      <div className="account-settings-page" style={{ paddingBottom: "4px" }}>
        <div className="account-breadcrumb">
          <Link to="/browse/users">Browse users</Link>
          {hasBrowseUserSelection ? (
            <span className="browse-users-breadcrumb-current">
              <span>{"/"}</span>
              <span>{activeUsername}</span>
            </span>
          ) : null}
        </div>

        <div className="account-settings-panel__header">
          <div>
            <h1 className="account-title">
              {hasBrowseUserSelection ? (
                <UserIdentityLabel username={activeUsername} tier={activeCollectorTier} size="md" color="#0f766e" wrap />
              ) : "Browse users"}
            </h1>
            <p className="account-copy">
              Search for other users on the marketplace and view their collections.
            </p>
          </div>
        </div>
      </div>

      {error ? (
        <div className="browse-users-alert browse-users-alert--error">
          {error}
        </div>
      ) : null}

      {tradeDraftNotice ? (
        <div className="browse-users-alert browse-users-alert--success browse-users-alert--floating">
          <span>{tradeDraftNotice}</span>
          {selectedBrowseUserId ? (
            <button
              type="button"
              onClick={() => navigate(`/tradefinder?counterpartyUserId=${selectedBrowseUserId}`)}
              className="browse-users-cta"
            >
              Open TradeFinder
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="app-responsive-split browse-users-layout">
        <aside className="browse-users-sidebar">
          <div className="browse-users-sidebar-card">
            <div className="browse-users-sidebar-title">Find user</div>
            <div ref={userSearchFieldRef} style={{ position: "relative" }}>
              <input
                type="search"
                value={usersSearchInput}
                placeholder="Type at least 3 letters"
                onChange={(event) => {
                  setUsersSearchInput(event.target.value);
                  setIsUserDropdownOpen(true);
                }}
                onFocus={() => setIsUserDropdownOpen(true)}
                className="browse-users-search-input"
              />
            </div>
            {userDropdown}
            <div className="browse-users-sidebar-actions">
              <button
                type="button"
                onClick={() => {
                  setUsersSearchInput("");
                  setUserSuggestions([]);
                  setUserSearchError("");
                  setIsUserDropdownOpen(false);
                  navigate("/browse/users");
                }}
                className="browse-users-secondary-button"
              >
                Reset
              </button>
            </div>
            <div className="browse-users-sidebar-meta">
              {trimmedUserSearchInput.length < 3
                ? "Type 3 letters to see user suggestions"
                : loadingUsers
                  ? "Loading users..."
                  : `${userSuggestions.length} suggestions`}
            </div>
          </div>
        </aside>

        {!hasBrowseUserSelection ? (
          <div style={{ minWidth: 0 }}>
            <div style={{ padding: isMobileFeaturedUsersLayout ? "0 20px 0" : "18px 20px", borderRadius: "18px", border: "1px solid #dbe4f0", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)", boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)", display: "grid", gap: isMobileFeaturedUsersLayout ? "0" : "14px" }}>
              <div style={{ display: isMobileFeaturedUsersLayout ? "none" : "flex", justifyContent: "space-between", gap: "12px", alignItems: "baseline", flexWrap: "wrap" }}>
                <div>
                  <div className="browse-users-filter-eyebrow">Discover users</div>
                </div>
              </div>

              {loadingFeaturedUsers ? (
                <div style={{ fontSize: "14px", color: "#64748b" }}>Loading users...</div>
              ) : null}

              {!loadingFeaturedUsers && featuredUsersError ? (
                <div style={{ fontSize: "14px", color: "#b91c1c" }}>{featuredUsersError}</div>
              ) : null}

              {!loadingFeaturedUsers && !featuredUsersError && featuredUsers.length === 0 ? (
                <div style={{ fontSize: "14px", color: "#64748b" }}>No users available right now.</div>
              ) : null}

              {!loadingFeaturedUsers && !featuredUsersError && featuredUsers.length > 0 ? (
                <div style={isMobileFeaturedUsersLayout
                  ? { overflow: "hidden", marginLeft: "-20px", marginRight: "-20px" }
                  : { border: "1px solid #dbe4f0", borderRadius: "14px", background: "#ffffff", overflow: "hidden" }}>
                  {isMobileFeaturedUsersLayout ? (
                    <div style={{ display: "grid" }}>
                      {featuredUsers.map((user, index) => {
                        const countryOption = getCountryOptionByName(user.country);

                        return (
                          <div
                            key={user.id}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "minmax(0, 50%) minmax(0, 30%) minmax(0, 20%)",
                              gap: "12px",
                              alignItems: "center",
                              padding: "12px 16px",
                              borderTop: index === 0 ? "none" : "1px solid #eef2f7",
                              color: "#0f172a"
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setUsersSearchInput(user.username);
                                navigate(buildBrowseUserPath(user.id, user.username), { state: { userId: user.id, username: user.username, collectorTier: user.collectorTier } });
                              }}
                              className="user-identity-link user-identity-link--truncate"
                              style={{ justifySelf: "stretch", display: "block", width: "100%", maxWidth: "100%", minWidth: 0, color: "#0f766e" }}
                            >
                              <UserIdentityLabel username={user.username} tier={user.collectorTier} size="sm" color="#0f766e" fontWeight={700} truncateAt={10} />
                            </button>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-start", minWidth: "18px", minHeight: "22px", flexShrink: 0 }}>
                                {countryOption?.flagCode ? (
                                  <span className={`fi fi-${countryOption.flagCode} account-setting-select-preview-flag`} aria-hidden="true" />
                                ) : (
                                  <span style={{ color: "#94a3b8", fontSize: "12px" }}>-</span>
                                )}
                              </span>
                              <span style={{ color: user.city ? "#334155" : "#94a3b8", minWidth: 0, fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.city || "-"}</span>
                            </span>
                            <span style={{ color: "#475569", fontWeight: 700, fontSize: "13px", whiteSpace: "nowrap", justifySelf: "end" }}>{user.listedCount}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ minWidth: 0, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                      <div style={{ minWidth: featuredUsersTableMinWidth }}>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: featuredUsersGridTemplate,
                            gap: "12px",
                            padding: "12px 16px",
                            background: "#f8fafc",
                            borderBottom: "1px solid #dbe4f0",
                            fontSize: "12px",
                            fontWeight: 800,
                            color: "#475569",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em"
                          }}
                        >
                          <span>User</span>
                          <span>Country</span>
                          <span>City</span>
                          <span>Traders</span>
                        </div>
                        <div style={{ display: "grid" }}>
                          {featuredUsers.map((user, index) => (
                            <div
                              key={user.id}
                              style={{
                                display: "grid",
                                gridTemplateColumns: featuredUsersGridTemplate,
                                gap: "12px",
                                alignItems: "center",
                                padding: "12px 16px",
                                borderTop: index === 0 ? "none" : "1px solid #eef2f7",
                                fontSize: "14px",
                                color: "#0f172a"
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setUsersSearchInput(user.username);
                                  navigate(buildBrowseUserPath(user.id, user.username), { state: { userId: user.id, username: user.username, collectorTier: user.collectorTier } });
                                }}
                                className="user-identity-link user-identity-link--truncate"
                                style={{ justifySelf: "start", minWidth: 0, color: "#0f766e" }}
                              >
                                <UserIdentityLabel username={user.username} tier={user.collectorTier} size="sm" color="#0f766e" fontWeight={700} truncateAt={10} />
                              </button>
                              <span style={{ color: user.country ? "#334155" : "#94a3b8", minWidth: 0, display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                {getCountryOptionByName(user.country)?.flagCode ? <span className={`fi fi-${getCountryOptionByName(user.country)?.flagCode} account-setting-select-preview-flag`} aria-hidden="true" /> : null}
                                <span>{user.country || "-"}</span>
                              </span>
                              <span style={{ color: user.city ? "#334155" : "#94a3b8", minWidth: 0 }}>{user.city || "-"}</span>
                              <span style={{ color: "#475569", fontWeight: 700 }}>{user.listedCount}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {hasBrowseUserSelection ? (
          <div style={{ minWidth: 0 }}>
            <div style={{ padding: "18px 20px", borderRadius: "18px", border: "1px solid #dbe4f0", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)", boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)", display: "grid", gap: "6px", width: "100%", maxWidth: "338px", boxSizing: "border-box" }}>
              <div className="browse-users-filter-eyebrow">User info</div>
              {activeDisplayName || activeCity || activeCountry ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                    gap: "6px 10px",
                    alignItems: "center"
                  }}
                >
                  <div style={{ gridColumn: "1 / 3", fontSize: "14px", fontWeight: 700, color: "#475569", lineHeight: 1.4 }}>
                    <span style={{ display: "block", minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                      {activeDisplayName || activeUsername}
                    </span>
                  </div>
                  <div style={{ fontSize: "14px", color: "#475569", fontWeight: 700, lineHeight: 1.4 }}>
                    {activeCountry || ""}
                  </div>
                  <div style={{ justifySelf: "start", display: "inline-flex", alignItems: "center" }}>
                    {activeCountryOption?.flagCode ? (
                      <span
                        className={`fi fi-${activeCountryOption.flagCode} account-setting-select-preview-flag`}
                        aria-hidden="true"
                        style={{ width: "29px", minWidth: "29px", height: "22px", borderRadius: "4px" }}
                      />
                    ) : null}
                  </div>
                  <div style={{ gridColumn: "1 / 3", fontSize: "14px", color: "#475569", fontWeight: 700, lineHeight: 1.4 }}>
                    {activeCity || ""}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: "14px", color: "#64748b", lineHeight: 1.6 }}>
                  This user has not added any profile information yet.
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <AdvancedFiltersModal
        isOpen={isAdvancedFiltersOpen}
        onClose={() => setIsAdvancedFiltersOpen(false)}
        title="Browse filters"
        activeCount={activeAdvancedFilterCount}
        onClear={clearBrowseAdvancedFilters}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Edition
            <AccountSettingSelect
              title="Edition"
              value={editionFilter}
              options={[
                { value: "", label: "All editions" },
                ...(filterMeta.editions || []).map((edition) => ({
                  value: edition.code,
                  label: `${setMetadata[normalizeLookupValue(edition.code)]?.name || edition.label}${edition.code ? ` (${String(edition.code).toUpperCase()})` : ""}`,
                  setCode: normalizeLookupValue(edition.code),
                  setMetadata: setMetadata[normalizeLookupValue(edition.code)],
                  showSetSymbol: Boolean(edition.code)
                }))
              ]}
              onChange={setEditionFilter}
              renderSelectedValue={(nextValue, option) => renderEditionOption(nextValue, option, "All editions")}
              renderOption={(nextValue, option) => renderEditionOption(nextValue, option)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Language
            <AccountSettingSelect
              title="Language"
              value={languageFilter}
              options={[
                { value: "", label: "All languages" },
                ...(filterMeta.languages || []).map((language) => ({ value: language, label: language }))
              ]}
              onChange={setLanguageFilter}
              renderSelectedValue={(nextValue) => nextValue ? renderLanguageOption(nextValue) : <span className="account-setting-select-preview-label">All languages</span>}
              renderOption={(nextValue) => nextValue ? renderLanguageOption(nextValue) : <span className="account-setting-select-preview-label">All languages</span>}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Condition
            <AccountSettingSelect
              title="Condition"
              value={conditionFilter}
              options={[
                { value: "", label: "All conditions" },
                ...(filterMeta.conditions || []).map((condition) => ({ value: condition, label: condition }))
              ]}
              onChange={setConditionFilter}
              renderSelectedValue={(nextValue) => nextValue ? renderConditionOption(nextValue) : <span className="account-setting-select-preview-label">All conditions</span>}
              renderOption={(nextValue) => nextValue ? renderConditionOption(nextValue) : <span className="account-setting-select-preview-label">All conditions</span>}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Foil
            <AccountSettingSelect
              title="Foil"
              value={foilFilter}
              options={FOIL_FILTER_OPTIONS}
              onChange={setFoilFilter}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Altered
            <AccountSettingSelect
              title="Altered"
              value={alteredFilter}
              options={ALTERED_FILTER_OPTIONS}
              onChange={setAlteredFilter}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Signed
            <AccountSettingSelect
              title="Signed"
              value={signedFilter}
              options={SIGNED_FILTER_OPTIONS}
              onChange={setSignedFilter}
            />
          </label>
        </div>
        <div className="browse-users-price-panel" style={{ marginTop: "16px" }}>
          <div className="browse-users-price-header">
            <span>Price range</span>
          </div>
          <div className="browse-users-price-row">
            <label className="browse-users-price-field">
              Min
              <input
                type="number"
                min={priceBounds.min}
                max={effectivePriceMax}
                step="0.5"
                value={priceMin ?? ""}
                placeholder={String(priceBounds.min)}
                disabled={!canAdjustPriceRange}
                onChange={(event) => handleBrowsePriceInputChange("min", event.target.value)}
                className="browse-users-input"
              />
            </label>
            <div className="browse-users-price-slider">
              <PriceRangeSlider
                min={priceBounds.min}
                max={priceBounds.max}
                step={0.5}
                valueMin={effectivePriceMin}
                valueMax={effectivePriceMax}
                disabled={!canAdjustPriceRange}
                onChange={handleBrowsePriceRangeChange}
              />
            </div>
            <label className="browse-users-price-field">
              Max
              <input
                type="number"
                min={effectivePriceMin}
                max={priceBounds.max}
                step="0.5"
                value={priceMax ?? ""}
                placeholder={String(priceBounds.max)}
                disabled={!canAdjustPriceRange}
                onChange={(event) => handleBrowsePriceInputChange("max", event.target.value)}
                className="browse-users-input"
              />
            </label>
          </div>
        </div>
      </AdvancedFiltersModal>

      {hasBrowseUserSelection ? (
        <section className="browse-users-content">
          <>
              <div className="browse-users-filter-panel">
                <div className="browse-users-filter-header">
                  <div>
                    <div className="browse-users-filter-eyebrow">
                      Filter cards
                    </div>
                    <div className="browse-users-filter-summary">
                      {pagination.totalItems} {resultsSummaryLabel} · {pagination.totalCards} cards total
                    </div>
                  </div>
                </div>
                <div className="app-responsive-filters browse-users-filters" style={{ display: "grid", gap: "14px" }}>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
                    <label className="browse-users-field browse-users-field--wide" style={{ flex: "1 1 320px", width: "min(420px, 100%)", minWidth: "280px" }}>
                        Search
                        <input
                          type="search"
                          value={searchInput}
                          placeholder="Card name, edition or collector #"
                          onChange={(event) => {
                            setSearchInput(event.target.value);
                            setSearchTerm(event.target.value.trim());
                          }}
                          className="browse-users-input"
                        />
                      </label>
                    <button
                      type="button"
                      onClick={() => setIsAdvancedFiltersOpen(true)}
                      className="browse-users-secondary-button"
                      style={{
                        background: activeAdvancedFilterCount > 0 ? "#ccfbf1" : "#ffffff",
                        color: activeAdvancedFilterCount > 0 ? "#115e59" : "#0f172a",
                        borderColor: activeAdvancedFilterCount > 0 ? "#0f766e" : undefined,
                        cursor: "pointer",
                        gap: "8px",
                        height: "42px",
                        flex: "0 0 auto"
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M2 3.5h12l-4.6 5.2v3.2l-2.8 1.6V8.7L2 3.5Z" fill="currentColor" />
                      </svg>
                      {activeAdvancedFilterCount > 0 ? `Filters (${activeAdvancedFilterCount})` : "Filters"}
                    </button>
                  </div>
                  <div className="browse-users-toggle-row" style={{ marginLeft: 0 }}>
                    <button
                      type="button"
                      aria-pressed={showSuggestedCards}
                      onClick={handleToggleShowSuggestedCards}
                      className="browse-users-toggle-button"
                      style={{
                        fontSize: "12px",
                        border: `1px solid ${showSuggestedCards ? "#0f766e" : "#cbd5e1"}`,
                        background: showSuggestedCards ? "#ccfbf1" : "#ffffff",
                        color: showSuggestedCards ? "#115e59" : "#334155",
                        boxShadow: showSuggestedCards ? "0 6px 14px rgba(20, 184, 166, 0.14)" : "none"
                      }}
                    >
                      <span style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background: showSuggestedCards ? "#0f766e" : "#cbd5e1",
                        flex: "0 0 auto"
                      }} />
                      {`Unavailable (${suggestedItemsCount})`}
                    </button>
                    <button
                      type="button"
                      aria-pressed={showAvailableCards}
                      onClick={handleToggleShowAvailableCards}
                      className="browse-users-toggle-button"
                      style={{
                        fontSize: "12px",
                        border: `1px solid ${showAvailableCards ? "#0f766e" : "#cbd5e1"}`,
                        background: showAvailableCards ? "#ccfbf1" : "#ffffff",
                        color: showAvailableCards ? "#115e59" : "#334155",
                        boxShadow: showAvailableCards ? "0 6px 14px rgba(20, 184, 166, 0.14)" : "none"
                      }}
                    >
                      <span style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background: showAvailableCards ? "#0f766e" : "#cbd5e1",
                        flex: "0 0 auto"
                      }} />
                      {`Available (${availableItemsCount})`}
                    </button>
                  </div>
                </div>
              </div>

              <div className="browse-users-view-toolbar">
                <div
                  className="browse-users-view-switch"
                  role="tablist"
                  aria-label="View mode"
                >
                  <div
                    aria-hidden="true"
                    className="browse-users-view-switch-indicator"
                    style={{ left: viewMode === "images" ? "4px" : "calc(50% + 2px)" }}
                  />
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === "images"}
                    title="View as images"
                    onClick={() => setViewMode("images")}
                    className="browse-users-view-switch-button"
                    style={{ color: viewMode === "images" ? "#0f172a" : "#64748b" }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                      <rect x="2" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
                      <rect x="11" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
                      <rect x="2" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
                      <rect x="11" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={viewMode === "text"}
                    title="View as text"
                    onClick={() => setViewMode("text")}
                    className="browse-users-view-switch-button"
                    style={{ color: viewMode === "text" ? "#0f172a" : "#64748b" }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                      <circle cx="3.5" cy="4" r="1.25" fill="currentColor" />
                      <circle cx="3.5" cy="9" r="1.25" fill="currentColor" />
                      <circle cx="3.5" cy="14" r="1.25" fill="currentColor" />
                      <rect x="6" y="3" width="9" height="2" rx="1" fill="currentColor" />
                      <rect x="6" y="8" width="9" height="2" rx="1" fill="currentColor" />
                      <rect x="6" y="13" width="9" height="2" rx="1" fill="currentColor" />
                    </svg>
                  </button>
                </div>
              </div>

              {showInitialLoadingState ? (
                <div style={{ color: "#475569" }}>Loading listed cards...</div>
              ) : cards.length === 0 ? (
                <div style={{ color: "#475569" }}>
                  {!showSuggestedCards && !showAvailableCards
                    ? "No cards selected."
                    : "No listed cards match the current filters."}
                </div>
              ) : (
                <div ref={resultsSectionRef}>
                  <PaginationControls
                    currentPage={Number(page) || 1}
                    totalItems={Number(pagination.totalItems) || cards.length}
                    totalPages={Math.max(1, Number(pagination.totalPages) || 1)}
                    label="Listed stacks"
                    onPageChange={handleBrowsePageChange}
                    disabled={loadingCards}
                  />

                  {loadingCards ? (
                    <div style={{ marginBottom: "14px", fontSize: "13px", color: "#64748b", fontWeight: 600 }}>
                      Loading listed cards...
                    </div>
                  ) : null}

                  {viewMode === "text" ? (
                    <CardsTable
                      cards={visibleBrowseCards.map(decorateBrowseCard)}
                      selectedCards={[]}
                      badgeType="LISTED"
                      userId={selectedUserId}
                      getDisplayQty={(card) => card.displayQty}
                      priceColumnLabel="Price"
                      isCardDisabled={isBrowseCardDisabled}
                      sortConfig={sortConfig}
                      onSortChange={handleTableSortChange}
                      renderRowAction={({ card, cardDisabled }) => renderTradeActionButton({ card, cardDisabled })}
                    />
                  ) : (
                    <CardGrid
                      cards={visibleBrowseCards.map(decorateBrowseCard)}
                      selectedCards={[]}
                      badgeType="LISTED"
                      isCardDisabled={isBrowseCardDisabled}
                      preferNormalImage={true}
                      stackOverlayActionOnMobile={true}
                      renderCardAction={({ card, cardDisabled }) => renderTradeActionButton({ card, cardDisabled })}
                    />
                  )}

                  {visibleBrowseCards.length === 0 ? (
                    <div style={{ color: "#64748b", marginTop: "16px", fontWeight: 600 }}>
                      {!showSuggestedCards && !showAvailableCards
                        ? "No cards selected."
                        : "No cards match the current suggestion filter."}
                    </div>
                  ) : null}

                  <PaginationControls
                    currentPage={Number(page) || 1}
                    totalItems={Number(pagination.totalItems) || cards.length}
                    totalPages={Math.max(1, Number(pagination.totalPages) || 1)}
                    label="Listed stacks"
                    onPageChange={handleBrowsePageChange}
                    disabled={loadingCards}
                    position="bottom"
                  />
                </div>
              )}
          </>
        </section>
      ) : null}
    </div>
  );
}